import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReservationFrameUnitMin, UpdatedByLabels } from 'types/common'
import { consultationMethodNames } from 'types/applicant'
import { StackItem } from 'ui/layout/StackItem'
import Section from 'ui/layout/Section'
import {
  formatYearDateJpDayOfWeekTime,
  parseDatetimeSecondString,
} from 'ui/utils/dateFormatUtils'
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { ThumbnailList } from 'ui/utils/Thumbnails'
import { Box, Button, Link } from '@mui/material'
import type { ReservationDetailProps } from 'types/med'
import { Text1, Text2 } from 'ui/shorthands/Texts'
import { reservationStatusLabelText } from 'ui/partials/reservationStatusLabelText'
import { howToReceiveMedicineValues } from 'ui/pages/applicant/Reservation/Reservation'
import ReservationDateModal, {
  ReservationDateModalProps,
} from '../ReservationDateModal'

const reformat = (dateTimeSecondString: string) =>
  formatYearDateJpDayOfWeekTime(parseDatetimeSecondString(dateTimeSecondString))

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    height: '100%',
    color: theme.palette.text.primary,
  },
  preWrap: {
    whiteSpace: 'pre-wrap',
  },
  attention: {
    color: theme.palette.secondary.main,
    backgroundColor: theme.palette.secondary.light,
    fontWeight: 'bold',
  },
}))

const SummarySection: React.FC<{
  reservation: ReservationDetailProps
  onChangeReservationDatetime: ReservationDateModalProps['onSubmit']
  unit: ReservationFrameUnitMin
}> = ({ reservation, onChangeReservationDatetime, unit }) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()
  const statusClass = cx(reservation.status === 'canceled' && classes.attention)
  const [reservationDateModalOpen, setReservationDateModalOpen] =
    useState<boolean>(false)

  const howToReceiveMedicineLabel = howToReceiveMedicineValues.find(
    ({ value }) => value === reservation.how_to_receive_medicine
  )?.label

  return (
    <Section>
      <div className={statusClass}>
        <StackItem
          label={<span className={statusClass}>{t('ステータス')}</span>}
        >
          <Text1 className={statusClass}>
            {reservationStatusLabelText(reservation)}
          </Text1>
        </StackItem>
      </div>
      <StackItem label={t('受付科目')}>
        <Text1>{reservation.reservation_department_name}</Text1>
      </StackItem>
      {reservation.reservation_datetime_start && (
        <StackItem label={t('予約日時')}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Text1>{reformat(reservation.reservation_datetime_start)}〜</Text1>
            <Button
              variant={'contained'}
              color={'primary'}
              onClick={() => setReservationDateModalOpen(true)}
              disabled={reservation.status !== 'before'}
            >
              {t('変更')}
            </Button>
            <ReservationDateModal
              unit={unit}
              currentDatetime={{
                start: reservation.reservation_datetime_start,
                end: reservation.reservation_datetime_end,
              }}
              open={reservationDateModalOpen}
              onCancel={() => setReservationDateModalOpen(false)}
              onSubmit={(data) =>
                onChangeReservationDatetime(data).finally(() =>
                  setReservationDateModalOpen(false)
                )
              }
            />
          </Box>
        </StackItem>
      )}
      <StackItem label={t('自由記述欄')} alignTop>
        <Text1 className={classes.preWrap}>
          {reservation.free_description}
        </Text1>
      </StackItem>
      {reservation.attachment_images && (
        <StackItem label={t('ファイル添付')} alignTop>
          <ThumbnailList images={reservation.attachment_images} />
        </StackItem>
      )}
      {reservation.consultation_method && (
        <StackItem label={t('受診方法')} alignTop>
          <Text1 className={classes.preWrap}>
            {consultationMethodNames[reservation.consultation_method]}
          </Text1>
        </StackItem>
      )}
      {reservation.consultation_method !== 'in_person' &&
        (reservation.call_method ? (
          <StackItem label={t('通話方法')} alignTop>
            <Text1>
              {reservation?.call_method_label
                ? t(reservation?.call_method_label)
                : null}
              {reservation.call_method.startsWith('video_') && (
                <div>
                  <Text2>
                    {t(
                      '予約日時になりましたら、下記のボタンからビデオ通話を開いてください。予約日時の15分前からビデオ通話が開けるようになります。'
                    )}
                  </Text2>
                </div>
              )}
              {reservation.call_method === 'phone' && (
                <>
                  :&nbsp;
                  <Link href={`tel:${reservation.applicant_phone}`}>
                    {reservation.applicant_phone}
                  </Link>
                </>
              )}
            </Text1>
          </StackItem>
        ) : (
          <StackItem label={t('連絡事項')} alignTop>
            <Text1 className={classes.preWrap}>
              {reservation?.reservation_message}
            </Text1>
          </StackItem>
        ))}
      {howToReceiveMedicineLabel && (
        <StackItem label={t('薬の受け取り方法')}>
          <Text1>
            {howToReceiveMedicineLabel ? t(howToReceiveMedicineLabel) : null}
          </Text1>
        </StackItem>
      )}
      <StackItem
        label={t('詳細')}
        toggleContent={
          <>
            {reservation.reservation_number && (
              <StackItem label={t('予約ID')}>
                <Text1>{reservation.reservation_number}</Text1>
              </StackItem>
            )}
            {reservation.created_at && (
              <StackItem label={t('予約作成日時')}>
                <Text1>{reformat(reservation.created_at)}</Text1>
              </StackItem>
            )}
            {reservation.updated_at && (
              <StackItem label={t('最終更新日時')}>
                <Text1>
                  {reformat(reservation.updated_at)}
                  {reservation.updated_by &&
                    UpdatedByLabels[reservation.updated_by] && (
                      <>&nbsp;{t(UpdatedByLabels[reservation.updated_by])}</>
                    )}
                </Text1>
              </StackItem>
            )}
            <StackItem label={t('予約方法')}>
              <Text1>
                {reservation.verification_type === 'sms'
                  ? t('SMS')
                  : reservation.verification_type === 'email'
                  ? t('メール')
                  : null}
              </Text1>
            </StackItem>
            <StackItem label={t('通知')}>
              <Text1>
                {reservation.verification_type === 'sms'
                  ? t(
                      '予約者に、予約前日の正午と予約開始の1時間前にSMSでリマインドします。'
                    )
                  : reservation.verification_type === 'email'
                  ? t(
                      '予約者に、予約前日の正午と予約開始の1時間前にメールでリマインドします。'
                    )
                  : null}
              </Text1>
            </StackItem>
          </>
        }
      >
        <Text1>{t('予約ID・予約作成日時・予約更新日時・通知')}</Text1>
      </StackItem>
    </Section>
  )
}

export default SummarySection
